import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FileValueInputProps {
  id: string;
  accept: string;
  invalid: boolean;
  /** The current value, as a data URI. */
  value: string;
  onChange: (dataUri: string) => void;
}

/**
 * Uploads become data URIs, which is the one shape both the image and table
 * pipelines already accept — so the client needs no format-specific handling.
 */
export function FileValueInput({ id, accept, invalid, value, onChange }: FileValueInputProps) {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState('');

  const read = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      onChange(typeof reader.result === 'string' ? reader.result : '');
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="mt-1">
      <input
        id={id}
        type="file"
        accept={accept}
        aria-invalid={invalid}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) read(file);
        }}
        className={`w-full rounded-sm border bg-surface px-3 py-2 text-sm outline-none transition file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-sm file:font-medium file:text-foreground focus:border-ring focus:ring-2 focus:ring-ring/22 ${
          invalid ? 'border-destructive' : 'border-input'
        }`}
      />

      {value ? (
        <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{fileName || t('wizard.form.fileChosen')}</span>
          <button
            type="button"
            onClick={() => {
              onChange('');
              setFileName('');
            }}
            className="underline underline-offset-2 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {t('common.remove')}
          </button>
        </p>
      ) : null}
    </div>
  );
}
